import * as Sentry from "@sentry/react";

// Errors that carry no signal for this app: dropped network failures and a few
// browser quirks. Matched as substrings of the error text.
const NOISE_MESSAGES = [
  "Network Error",
  "Request aborted",
  "Load failed",
  "getModifierState is not a function",
  "Non-Error promise rejection captured",
];

// Releases reported by browser extensions that share this DSN. App releases
// are a build commit SHA (see craco.config.js), which none of these match.
const EXTENSION_RELEASES = [
  /^browser-extension@/,
  /^koala-inspector@/,
  /^app-extension@/,
  /^rbc-chrome-extension/,
  /-chrome$/,
  /-edge$/,
];

// "Failed to fetch" is the Chrome text for a request that never got an answer.
// It is only noise when nothing caught the rejection (onunhandledrejection) and
// a browser extension (content blockers wrap window.fetch) or a Google tag
// script is in the stack: the app cannot act on either.
const FETCH_FAILURE = "Failed to fetch";
const THIRD_PARTY_FRAME =
  /^(?:chrome|moz|safari|safari-web)-extension:\/\/|^https?:\/\/(?:www\.)?(?:googletagmanager|google-analytics)\.com\//;

// Monaco and its language workers are third-party code, served from
// /static/monaco/ or from the CDN. An error that is thrown only inside their
// files has no frame of this app, so the app cannot act on it. Frames of the
// Sentry SDK wrappers and native code do not count. Only the known Monaco
// errors are dropped, so a new Monaco defect stays visible.
const MONACO_MESSAGES = [
  "Illegal value for lineNumber",
  "reading 'completion'",
  "this._completionModel.getItemsByProvider",
  "Maximum call stack size exceeded",
  "reading 'getText'",
];
const MONACO_FRAME = /\/(?:static\/monaco|npm\/monaco-editor@[^/]+\/min)\/vs\//;
const WRAPPER_FRAME = /@sentry\/|^\[native code\]$|^<anonymous>$/;

const frameFile = (frame) => frame.abs_path || frame.filename || "";

// Monaco could not start a web worker. It logs this line to the console and
// runs the worker code in the main thread, so the editor keeps working. The
// browser also fires an error Event at the Worker, which the SDK reports as an
// exception with no message and no frame of the app. The breadcrumb ties the
// Event to Monaco: other scripts of the page (analytics) can start workers too.
const MONACO_WORKER_FALLBACK = "Could not create web worker(s)";
const WORKER_ERROR_EVENT = "[object Worker]";

// A browser extension that adds a JSON-RPC provider to the page (for example a
// wallet) can reject a request with this plain object. Nothing in the app uses
// JSON-RPC, and the rejection has no stack, so the app cannot act on it. The
// SDK keeps the rejected object in extra.__serialized__.
const JSON_RPC_INTERNAL_ERROR_CODE = -32603;
const JSON_RPC_INTERNAL_ERROR_MESSAGE = "Internal JSON-RPC error.";

// After a deploy, a tab that stays open asks for a script file that the new
// build no longer has, and the host answers with index.html. The browser fails to
// run that page as a script: "Unexpected token '<'", raised by window.onerror in
// a file of /static/js/. The ChunkLoadError of the same load still reports the
// condition, so this syntax error adds nothing.
const HTML_AS_SCRIPT = "Unexpected token '<'";
const APP_SCRIPT_FRAME = /\/static\/js\/[^/]+\.js$/;

// A Monaco file that the browser could not download (offline, blocked or a
// closed connection). The worker fails in importScripts, and the Monaco loader
// reports the failed script as an Event object. The student network causes
// both, and the app cannot act on them.
const MONACO_WORKER_LOAD_FAILURE =
  /Failed to execute 'importScripts' on 'WorkerGlobalScope'.*\/monaco/s;
const MONACO_LOADER_LOAD_FAILURE =
  /^\[object Event\][\s\S]*(?:monaco-editor@[^/]+\/min|static\/monaco)\/vs\/loader\.js/;
const MONACO_LOADER_FRAME =
  /(?:monaco-editor@[^/]+\/min|static\/monaco)\/vs\/loader\.js/;
const OBJECT_EVENT = "[object Event]";

// A request that nobody caught and that the server answered with 401: the
// session ended (the token refresh failed, the API client cleared the session)
// and the page is on its way to the login page. Nothing is left to fix.
const UNAUTHORIZED = "Request failed with status code 401";

// Microsoft Clarity (loaded by public/index.html) adds a script node to the page
// and, on some Chromebooks, the browser rejects it: "Failed to execute
// 'appendChild' on 'Node': Unexpected identifier 'as'". The error is inside the
// third-party script. It is dropped only when every frame is anonymous, so an
// error that has a frame of the app still reports.
const INJECTED_SCRIPT_ERROR =
  /^Failed to execute 'appendChild' on 'Node': Unexpected identifier/;

// MathLive (served from /static/mathlive/) releases the pointer capture of a
// pointer that the page no longer holds. WebKit on iOS then throws NotFoundError
// in the native releasePointerCapture. Only MathLive and the native call are in
// the stack, so the app cannot act on it.
const MATHLIVE_FRAME = /\/static\/mathlive\/mathlive(?:\.min)?\.js$/;

const eventMessages = (event) => [
  event.message,
  ...((event.exception && event.exception.values) || []).map((v) => v.value),
];

export const isNoiseEvent = (event) =>
  eventMessages(event).some(
    (text) =>
      typeof text === "string" && NOISE_MESSAGES.some((m) => text.includes(m)),
  );

export const isThirdPartyFetchFailure = (event) =>
  ((event.exception && event.exception.values) || []).some(
    (v) =>
      v.value === FETCH_FAILURE &&
      v.mechanism &&
      v.mechanism.type === "onunhandledrejection" &&
      ((v.stacktrace && v.stacktrace.frames) || []).some((frame) =>
        THIRD_PARTY_FRAME.test(frame.filename || frame.abs_path || ""),
      ),
  );

const breadcrumbList = (event) =>
  Array.isArray(event.breadcrumbs)
    ? event.breadcrumbs
    : (event.breadcrumbs && event.breadcrumbs.values) || [];

export const isMonacoWorkerStartFailure = (event) => {
  const failed = event.extra && event.extra.__serialized__;
  return (
    !!failed &&
    failed.type === "error" &&
    failed.target === WORKER_ERROR_EVENT &&
    breadcrumbList(event).some(
      (crumb) =>
        typeof crumb.message === "string" &&
        crumb.message.includes(MONACO_WORKER_FALLBACK),
    )
  );
};

export const isExtensionRpcRejection = (event) => {
  const rejected = event.extra && event.extra.__serialized__;
  return (
    !!rejected &&
    rejected.code === JSON_RPC_INTERNAL_ERROR_CODE &&
    rejected.message === JSON_RPC_INTERNAL_ERROR_MESSAGE &&
    ((event.exception && event.exception.values) || []).some(
      (v) => v.mechanism && v.mechanism.type === "onunhandledrejection",
    )
  );
};

export const isHtmlServedAsScript = (event) =>
  ((event.exception && event.exception.values) || []).some((v) => {
    const frames = (v.stacktrace && v.stacktrace.frames) || [];
    return (
      v.type === "SyntaxError" &&
      v.value === HTML_AS_SCRIPT &&
      v.mechanism &&
      v.mechanism.type === "onerror" &&
      frames.length > 0 &&
      frames.every((frame) =>
        APP_SCRIPT_FRAME.test(
          (frame.filename || frame.abs_path || "").split("?")[0],
        ),
      )
    );
  });

export const isMonacoLoadFailure = (event) =>
  eventMessages(event).some(
    (text) =>
      typeof text === "string" &&
      (MONACO_WORKER_LOAD_FAILURE.test(text) ||
        MONACO_LOADER_LOAD_FAILURE.test(text)),
  ) ||
  ((event.exception && event.exception.values) || []).some(
    (v) =>
      v.value === OBJECT_EVENT &&
      ((v.stacktrace && v.stacktrace.frames) || []).some((frame) =>
        MONACO_LOADER_FRAME.test(frame.filename || frame.abs_path || ""),
      ),
  );

export const isUnhandledUnauthorized = (event) =>
  ((event.exception && event.exception.values) || []).some(
    (v) =>
      v.value === UNAUTHORIZED &&
      v.mechanism &&
      v.mechanism.type === "onunhandledrejection",
  );

// The Facebook in-app browser injects its own script into every page. That
// script fails on "window._META.IABInnerFrame". The frame is the page URL and
// no file of this app is in the stack, so the app cannot act on it.
const IN_APP_BROWSER_SCRIPT = "window._META.IABInnerFrame";

export const isInAppBrowserScriptError = (event) =>
  eventMessages(event).some(
    (text) => typeof text === "string" && text.includes(IN_APP_BROWSER_SCRIPT),
  );

// Monaco rejects a promise with an error named "Canceled" when it stops an
// editor action on purpose (a paste or a request that a newer one replaces).
// It is the normal end of that action, not a fault.
export const isMonacoCancellation = (event) =>
  ((event.exception && event.exception.values) || []).some(
    (v) => v.type === "Canceled" && v.value === "Canceled",
  );

export const isAnonymousScriptSyntaxError = (event) =>
  ((event.exception && event.exception.values) || []).some((v) => {
    const frames = (v.stacktrace && v.stacktrace.frames) || [];
    return (
      typeof v.value === "string" &&
      INJECTED_SCRIPT_ERROR.test(v.value) &&
      frames.length > 0 &&
      frames.every(
        (frame) => (frame.filename || frame.abs_path) === "<anonymous>",
      )
    );
  });

export const isMathLivePointerError = (event) =>
  ((event.exception && event.exception.values) || []).some((v) => {
    const frames = ((v.stacktrace && v.stacktrace.frames) || []).filter(
      (frame) => frame.function !== "sentryWrapped",
    );
    return (
      v.type === "NotFoundError" &&
      frames.some((frame) => frame.function === "releasePointerCapture") &&
      frames.every(
        (frame) =>
          frame.filename === "[native code]" ||
          MATHLIVE_FRAME.test(frame.filename || frame.abs_path || ""),
      )
    );
  });

export const isMonacoInternalError = (event) =>
  ((event.exception && event.exception.values) || []).some((v) => {
    if (
      typeof v.value !== "string" ||
      !MONACO_MESSAGES.some((m) => v.value.includes(m))
    ) {
      return false;
    }
    const frames = ((v.stacktrace && v.stacktrace.frames) || []).filter(
      (frame) =>
        frame.function !== "sentryWrapped" &&
        !WRAPPER_FRAME.test(frameFile(frame)),
    );
    return (
      frames.length > 0 &&
      frames.every((frame) => MONACO_FRAME.test(frameFile(frame)))
    );
  });

export const isExtensionEvent = (event) =>
  typeof event.release === "string" &&
  EXTENSION_RELEASES.some((re) => re.test(event.release));

// Returning null drops the event.
export const beforeSend = (event) =>
  isNoiseEvent(event) ||
  isThirdPartyFetchFailure(event) ||
  isMonacoInternalError(event) ||
  isMonacoWorkerStartFailure(event) ||
  isExtensionRpcRejection(event) ||
  isMathLivePointerError(event) ||
  isHtmlServedAsScript(event) ||
  isMonacoLoadFailure(event) ||
  isUnhandledUnauthorized(event) ||
  isInAppBrowserScriptError(event) ||
  isMonacoCancellation(event) ||
  isAnonymousScriptSyntaxError(event) ||
  isExtensionEvent(event)
    ? null
    : event;

// The environment tag is REACT_APP_ENVIRONMENT, trimmed and lower-cased (the
// variable the feature flags use for their tier). When it is unset the tag is
// "unknown", never "production": the SDK would otherwise fill in "production".
// No release is set here: the build plugin in craco.config.js injects the
// release into the bundle and the SDK reads it from there.
export const sentryEnvironment = (env = process.env) =>
  String(env.REACT_APP_ENVIRONMENT || "")
    .trim()
    .toLowerCase() || "unknown";

// The one test for "may this build send events". Every Sentry.capture* call
// goes through the client that Sentry.init creates, so a disabled client
// drops them all. Events go out only from a production build, or when
// REACT_APP_SENTRY_ENABLED is the exact string "true" (staging debug session).
export const sentryEnabled = (env = process.env) =>
  sentryEnvironment(env) === "production" ||
  env.REACT_APP_SENTRY_ENABLED === "true";

export const sentryOptions = () => ({
  ignoreErrors: [
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications",
  ],
  // Env-driven so stage builds can point elsewhere; falls back to the prod DSN
  // so an unconfigured build still reports.
  dsn:
    process.env.REACT_APP_SENTRY_DSN ||
    "https://8cba7814fb4146fba6e84192c51f4ced@o877499.ingest.sentry.io/6175607",
  integrations: [Sentry.browserTracingIntegration()],
  // 1.0 shipped a full trace for every session and burned transaction quota.
  tracesSampleRate: 0.1,
  beforeSend,
  environment: sentryEnvironment(),
  enabled: sentryEnabled(),
});

// Embedded in StudentPortal, both bundles share one global Sentry client (same
// SDK version). A second init would replace the portal client and send portal
// events to this project, so the embedded sandbox leaves the portal client alone.
export const initSentry = () => {
  if (typeof window !== "undefined" && window.__SANDBOX_IS_PLUGIN__)
    return undefined;
  return Sentry.init(sentryOptions());
};
